import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Card, Progress, Spin, Tabs, Flex, Typography, message } from 'antd';
import classNames from 'classnames';
import {
  EditOutlined,
  SyncOutlined,
  UserOutlined,
  DisconnectOutlined,
  LineChartOutlined,
  PhoneOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { UserDisplay } from '../../../../pages/users/types/UserDisplay';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { getUserStatusBadgeClass, isUnlimitedLeedbucks, getPlanCreditsRemaining, getPlanCreditsOverageState } from '../../../../helpers';
import CompanyContext from '../../../../contexts/company/CompanyContext';
import AuthenticationContext from '../../../../contexts/auth/AuthenticationContext';
import SubscriptionContext from '../../../../contexts/subscription/SubscriptionContext';
import { updatePaygSetting } from '../../../../../domain/use_cases/subscription/updatePaygSetting';
import { getUserStatsForPeriod, UserLeedflowStatsForPeriod } from '../../../../../domain/use_cases/leedflow/getUserStatsForPeriod';
import { useLeedbucksConfig } from '../../../../hooks/plans/useLeedbucksConfig';
import { ECreditType } from '@leedpulse-monorepo/enums';
import ActivatePaygModal from '../activate-payg/ActivatePaygModal';
import SubscriptionConsumptionHistory from '../../../@shared/consumption-history/SubscriptionConsumptionHistory';

import styles from './ViewProfileModal.module.css';

const { Text } = Typography;

const EMPTY_STATS: UserLeedflowStatsForPeriod = {
  callsCount: 0,
  callDurationMinutes: 0,
  smsReceivedCount: 0,
  smsSentCount: 0,
};


interface ViewProfileModalProps {
  isOpen: boolean;
  user: UserDisplay | null;
  onClose: () => void;
  onEdit?: () => void;
  onChangeNumber?: () => void;
  onDeactivate?: () => void;
  onReactivate?: () => void;
}

const formatCallTime = (minutes: number): string => {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return remainingMinutes === 0 ? `${hours}h` : `${hours}h${remainingMinutes.toString().padStart(2, '0')}`;
};

const formatNumber = (value: number): string => value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

// What credits buy at a rate: unlimited for a free action, a dash until the rates are loaded
const equivalent = (credits: number, rate: number | undefined, unit: string, round = Math.floor): string =>
  rate === undefined ? `- ${unit}` : rate === 0 ? 'Illimités' : `${formatNumber(round(credits / rate))} ${unit}`;
const formatEuro = (value: number): string => value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ViewProfileModal: React.FC<ViewProfileModalProps> = ({
  isOpen,
  user,
  onClose,
  onEdit,
  onChangeNumber,
  onDeactivate,
  onReactivate,
}) => {
  const { activeCompany } = useContext(CompanyContext);
  const { authentication } = useContext(AuthenticationContext);
  const { subscriptions: companySubscriptions, refreshSubscriptions } = useContext(SubscriptionContext);
  // Its number's own rates: its promo or override first, the global ones for the rest
  const { callRate, smsRate, isError: ratesUnavailable } = useLeedbucksConfig({ subscriptionId: user?.idSubscription });

  // PAYG (hors forfait) : override local pour refléter immédiatement l'activation depuis la bannière.
  const [paygOverride, setPaygOverride] = useState<boolean | null>(null);
  const [paygSaving, setPaygSaving] = useState(false);
  const [paygModalOpen, setPaygModalOpen] = useState(false);

  const isInactive = user?.status === 'Inactif';
  const showDeactivate = user?.status === 'Actif' || user?.status === 'Invité';

  const [userStats, setUserStats] = useState<UserLeedflowStatsForPeriod>(EMPTY_STATS);
  const [statsLoading, setStatsLoading] = useState(false);

  // PAYG rate comes from the consumption history (used to price the overage banner).
  const [paygRate, setPaygRate] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      if (!isOpen || !user?.userDoc?.uid || !activeCompany?.api_key || !authentication?.token) {
        setStatsLoading(false);
        return;
      }
      setStatsLoading(true);
      try {
        const stats = await getUserStatsForPeriod({ userUid: user.userDoc.uid }, activeCompany.api_key, authentication);
        if (cancelled) return; // stale response, selection has changed
        setUserStats(stats);
      } catch (e) {
        console.error('ViewProfileModal – error fetching user stats:', e);
        if (cancelled) return;
        setUserStats(EMPTY_STATS);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [isOpen, user?.userDoc?.uid, activeCompany?.api_key, authentication]);

  const creditsInfo = user?.credits_info;

  const planCredits = useMemo(() => {
    if (!creditsInfo) return null;
    const total = creditsInfo.total?.[ECreditType.leedbucks] || 0;
    const current = creditsInfo.remaining?.[ECreditType.leedbucks] || 0;
    const isUnlimited = creditsInfo.isUnlimitedLeedbucks || isUnlimitedLeedbucks(total);
    const r = getPlanCreditsRemaining(total, current, isUnlimited);
    const consumed = Math.max(0, total - current);
    const overageCredits = Math.max(0, consumed - total);
    const remaining = Math.max(0, current);
    // Bannière unique : 'disabled' (PAYG coupé) > 'overage' (PAYG actif) > 'warning' (1-20 %).
    const effectivePayg = paygOverride !== null ? paygOverride : creditsInfo.payg_enabled;
    const state: 'disabled' | 'overage' | 'warning' | null =
      getPlanCreditsOverageState(r.exhausted, effectivePayg) ?? (r.nearlyEmpty ? 'warning' : null);
    return {
      total,
      isUnlimited,
      remainingPercent: r.remainingPercent,
      nearlyEmpty: r.nearlyEmpty,
      exhausted: r.exhausted,
      amber: r.amber,
      barColor: r.barColor,
      state,
      overageAmount: overageCredits * paygRate,
      minutesRemaining: equivalent(remaining, callRate, 'min'),
      smsRemaining: equivalent(remaining, smsRate, 'SMS'),
      overageMinutes: equivalent(overageCredits, callRate, 'min', (n) => Math.round(n / 5) * 5),
      overageSms: equivalent(overageCredits, smsRate, 'SMS'),
    };
  }, [creditsInfo, paygRate, callRate, smsRate, paygOverride]);

  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const paygResetKey = `${isOpen}:${user?.idSubscription ?? ''}`;
  const [lastPaygResetKey, setLastPaygResetKey] = useState(paygResetKey);
  if (paygResetKey !== lastPaygResetKey) {
    setLastPaygResetKey(paygResetKey);
    setPaygOverride(null);
  }

  const subApiKey = companySubscriptions.find((s) => s.id === user?.idSubscription)?.api_key;

  const confirmActivatePayg = async () => {
    if (!subApiKey || !authentication?.token) return;
    setPaygSaving(true);
    try {
      await updatePaygSetting(subApiKey, true, authentication);
      setPaygOverride(true);
      message.success('Hors forfait activé');
      refreshSubscriptions();
      setPaygModalOpen(false);
    } catch (e) {
      message.error((e as Error)?.message || 'Impossible de modifier ce réglage.');
    } finally {
      setPaygSaving(false);
    }
  };

  if (!user) return null;

  const informationsTab = (
    <Flex vertical gap={"1.25rem"}>
      <Flex vertical gap={6} className={styles.detailCard}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Numéro Leedflow</span>
          <span className={styles.detailValue}>
            {user.sender.length > 0 ? user.sender.map((sender) => formatPhoneNumber(sender)).join(', ') : '-'}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Plan</span>
          {user.plan ? (
            <span className={styles.planTag}>{user.plan}</span>
          ) : (
            <span className={styles.detailValue}>-</span>
          )}
        </div>

        {/* Forfait restant */}
        {planCredits && (
          <div className={styles.planCredits}>
            {planCredits.isUnlimited ? (
              <Flex align="center" justify="space-between">
                <Text type="secondary">Forfait restant</Text>
                <Text>Illimité</Text>
              </Flex>
            ) : (
              <Flex vertical gap={10}>
                <Flex align="baseline" justify="space-between" gap={12}>
                  <Text type="secondary">Forfait restant</Text>
                  <span className={classNames(styles.remainingPercent, planCredits.amber && styles.remainingPercentAmber)}>{planCredits.remainingPercent} %</span>
                </Flex>
                <Progress
                  percent={planCredits.remainingPercent}
                  showInfo={false}
                  strokeColor={planCredits.barColor}
                  style={{ margin: 0 }}
                />
                <div className={styles.equivalences}>
                  <div className={styles.equivalenceCard}>
                    <Text type="secondary" className={styles.equivalenceLabel}>
                      <PhoneOutlined className={styles.icon} />Appels restants
                    </Text>
                    <Text strong>{planCredits.minutesRemaining}</Text>
                  </div>
                  <div className={styles.equivalenceCard}>
                    <Text type="secondary" className={styles.equivalenceLabel}>
                      <MessageOutlined className={styles.icon} />SMS restants
                    </Text>
                    <Text strong>{planCredits.smsRemaining}</Text>
                  </div>
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {ratesUnavailable ? (
                    <>
                      <WarningOutlined className={styles.icon} />
                      Équivalences appels / SMS indisponibles pour le moment.
                    </>
                  ) : (
                    <>
                      <InfoCircleOutlined className={styles.icon} />
                      Appels et SMS utilisent le même forfait. Chaque appel ou SMS réduit les deux compteurs.
                    </>
                  )}
                </Text>

                {/* Bannière unique d'état */}
                {planCredits.state === 'disabled' ? (
                  <div className={styles.planCreditsBanner}>
                    <StopOutlined className={styles.planCreditsBannerIcon} />
                    <div style={{ flex: 1 }}>
                      <div className={styles.planCreditsBannerTitle}>Hors forfait désactivé</div>
                      <div className={styles.planCreditsBannerText} style={{fontSize: ".625rem"}}>
                        <Flex gap={4}>
                          <span className={styles.strong}>Appels entrants et sortants :</span>
                          <span>possibles, mais sans enregistrement, transcription ni résumé IA.</span>
                        </Flex>
                        <Flex gap={4}>
                          <span className={styles.strong}>SMS reçus :</span>
                          <span>visibles, mais sans analyse IA.</span>
                        </Flex>
                        <Flex gap={4}>
                          <span className={styles.strong}>SMS envoyés :</span>
                          <span>envoi bloqué.</span>
                        </Flex>
                      </div>
                      {subApiKey && (
                        <Button size="small" className={styles.planCreditsBannerBtn} loading={paygSaving} onClick={() => setPaygModalOpen(true)}>
                          Activer le hors forfait
                        </Button>
                      )}
                    </div>
                  </div>
                ) : planCredits.state === 'overage' ? (
                  <div className={styles.planCreditsBanner}>
                    <div style={{ flex: 1 }}>
                      <Flex align="center" justify="space-between" gap={10}>
                        <span className={styles.planCreditsBannerTitle}>Hors forfait en cours</span>
                        {planCredits.overageAmount > 0 && <span className={styles.planCreditsBannerAmount}>{formatEuro(planCredits.overageAmount)} €</span>}
                      </Flex>
                      <div className={styles.planCreditsBannerText}>
                        <div className={styles.planCreditsBannerTextPayg}>
                          <Flex gap={4}>
                            Appels :
                            <span className={styles.strong}>{planCredits.overageMinutes}</span>
                          </Flex>
                          <Flex gap={4}>
                            SMS :
                            <span className={styles.strong}>{planCredits.overageSms}</span>
                          </Flex>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : planCredits.state === 'warning' ? (
                  <div className={styles.planCreditsBanner}>
                    <WarningOutlined className={styles.planCreditsBannerIcon} />
                    <div>
                      <div className={styles.planCreditsBannerTitle}>Forfait bientôt épuisé</div>
                      <div className={styles.planCreditsBannerText}>Il reste {planCredits.remainingPercent} % du forfait.</div>
                    </div>
                  </div>
                ) : null}
              </Flex>
            )}
          </div>
        )}
      </Flex>

      {/* Utilisation du cycle en cours */}
      <Card className={styles.statsCard}>
        <div className={styles.statsCardHeader}>
          <div className={styles.statsCardIcon}>
            <LineChartOutlined style={{ fontSize: 20 }} />
          </div>
          <span className={styles.statsCardTitle}>UTILISATION DU CYCLE</span>
        </div>
        <Spin spinning={statsLoading}>
          <div className={styles.stats}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{statsLoading ? '-' : userStats.callsCount.toLocaleString('fr-FR')}</span>
              <span className={styles.statLabel}>Appels</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{statsLoading ? '-' : formatCallTime(userStats.callDurationMinutes)}</span>
              <span className={styles.statLabel}>Temps d'appel</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{statsLoading ? '-' : userStats.smsReceivedCount.toLocaleString('fr-FR')}</span>
              <span className={styles.statLabel}>SMS reçus</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{statsLoading ? '-' : userStats.smsSentCount.toLocaleString('fr-FR')}</span>
              <span className={styles.statLabel}>SMS envoyés</span>
            </div>
          </div>
        </Spin>
      </Card>

      <Flex vertical>
        <div className={styles.memberInfo}>
          Membre depuis : {user.memberSince} • Dernière activité : {user.lastActivity ?? 'N/A'}
        </div>

        <div className={styles.actions}>
          <Button icon={<EditOutlined />} className={styles.actionBtn} onClick={onEdit}>Modifier</Button>
          <Button icon={<SyncOutlined />} className={styles.actionBtn} onClick={onChangeNumber}>Changer le numéro</Button>
          {showDeactivate && (
            <Button icon={<DisconnectOutlined />} className={classNames(styles.actionBtn, styles.actionBtnDanger)} onClick={onDeactivate} disabled>Désassigner</Button>
          )}
          {isInactive && (
            <Button icon={<UserOutlined />} className={classNames(styles.actionBtn, styles.actionBtnPrimary)} onClick={onReactivate}>Réactiver</Button>
          )}
        </div>
      </Flex>
    </Flex>
  );

  const historyTab = (
    <SubscriptionConsumptionHistory
      open={isOpen}
      idSubscription={user?.idSubscription}
      onHistoryLoaded={(h) => setPaygRate(h.payg_rate ?? 0)}
    />
  );

  return (
    <>
    <Modal open={isOpen} onCancel={onClose} footer={null} centered width={650} className={styles.modal} title={user.name}>
      <div className={styles.content}>
        {/* User Header */}
        <div className={styles.userHeader}>
          <div className={styles.avatarContainer}>
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className={styles.avatar} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                <UserOutlined />
              </div>
            )}
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userContact}>{user.email} - {user.phone || 'Non renseigné'}</span>
            <span className={classNames(styles.statusBadge, getUserStatusBadgeClass(user.status))}>{user.status}</span>
          </div>
        </div>

        <Tabs
          defaultActiveKey="informations"
          items={[
            { key: 'informations', label: 'Informations', children: informationsTab },
            { key: 'history', label: 'Historique de consommation', children: historyTab },
          ]}
        />
      </div>
    </Modal>

    <ActivatePaygModal
      isOpen={paygModalOpen}
      isLoading={paygSaving}
      onCancel={() => setPaygModalOpen(false)}
      onConfirm={confirmActivatePayg}
    />
    </>
  );
};

export default ViewProfileModal;
